/**
 * `useShortfallOptions` — TanStack Query hooks for the shortfall
 * call-back ladder (`GET /api/cycles/{id}/shortfalls?date=…` plus the two
 * write rungs `POST …/shortfalls/recall` and `POST …/shortfalls/cancel-leave`).
 *
 * Lead-only. The department's practice, in order: seat someone who is
 * simply free (rung 0, `useConfirmSeat` → `POST …/shortfalls/seat`, founder
 * test 2 Oct 2026); call back someone who is post-call; failing that, cancel
 * someone's leave. All are the rosterer's call, never automatic — the
 * endpoints only write when the lead confirms a specific person (no
 * establishment ceiling, no "just add a person"). When no rung has anyone
 * the gap stays visible and the payload's `empty_note` says so. A pooled group floor short across
 * its wards (Build 20) is a cell of `kind: "floor"`; its candidates are the
 * floor's grades only and Confirm places the person on the cell's `station`
 * (the group's ward with the widest gap).
 *
 * Who is post-call follows each on-call type's own rest rule (rest days,
 * AM-only, hard or soft) — the backend's `insights.post_call`. A recall's
 * response also carries a `rest_offer`: the first following day the person
 * could be given back without opening another gap; `useConfirmRest` writes
 * it (`POST …/shortfalls/rest`) — their ward rows that day are released and
 * a confirmed rest day recorded. Still the lead's click, never automatic.
 *
 * Consumed by `<ShortfallOptionsModal/>`, opened from the roster grid's
 * day header ("Short · options").
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";
import { liveRefetchOptions } from "./liveRefetch";
import type { components } from "./schema.gen";
import { coverageReviewQueryFilter } from "./useCoverageReview";
import { cycleRosterQueryFilter } from "./useCycleRoster";
import { mcAttentionQueryKey } from "./useMcAttention";

export type ShortfallOptionsResponse = components["schemas"]["ShortfallOptionsResponse"];
export type ShortfallCell = components["schemas"]["ShortfallCell"];
export type RecallCandidate = components["schemas"]["RecallCandidate"];
export type CancelLeaveCandidate = components["schemas"]["CancelLeaveCandidate"];
export type FreeCandidate = components["schemas"]["FreeCandidate"];
export type OpenTo = components["schemas"]["OpenTo"];
export type OpenToGroup = components["schemas"]["OpenToGroup"];
export type OpenToPerson = components["schemas"]["OpenToPerson"];
export type ShortfallMoveCandidate = components["schemas"]["ShortfallMoveCandidate"];
export type MoveWriteResponse = components["schemas"]["MoveWriteResponse"];
export type SeatWriteResponse = components["schemas"]["SeatWriteResponse"];
export type ShortfallWriteResponse = components["schemas"]["ShortfallWriteResponse"];
export type RestOffer = components["schemas"]["RestOffer"];
export type RestWriteResponse = components["schemas"]["RestWriteResponse"];

const STALE_TIME_MS = 15_000;

export function shortfallOptionsQueryKey(
  cycleId: string,
  date: string,
  accountId: string | null,
): readonly unknown[] {
  return ["cycle-shortfall-options", cycleId, date, accountId ?? ""];
}

/** Invalidation filter for every cached ladder read of one cycle (any day,
 *  any account) — for callers that change the roster without holding the
 *  date + accountId the key carries. */
export function shortfallOptionsQueryFilter(cycleId: string): {
  predicate: (query: { queryKey: readonly unknown[] }) => boolean;
} {
  return {
    predicate: (query) =>
      query.queryKey[0] === "cycle-shortfall-options" && query.queryKey[1] === cycleId,
  };
}

interface UseShortfallOptionsOpts {
  /** Force-disable the fetch (the panel only fetches while it is open). */
  enabled?: boolean;
}

export function useShortfallOptions(
  cycleId: string,
  date: string | null,
  opts: UseShortfallOptionsOpts = {},
) {
  const { accountId } = useAuth();
  const enabledByCaller = opts.enabled ?? true;
  return useQuery<ShortfallOptionsResponse, ApiError | Error>({
    queryKey: shortfallOptionsQueryKey(cycleId, date ?? "", accountId),
    enabled: Boolean(cycleId && date && accountId) && enabledByCaller,
    staleTime: STALE_TIME_MS,
    retry: false,
    // #90 — the ladder's cells are what is short RIGHT NOW: a report that
    // lands while the panel is open must show up. `enabled` is the open
    // flag, so nothing polls once the panel closes. See `liveRefetch.ts`.
    ...liveRefetchOptions,
    queryFn: () =>
      apiFetch<ShortfallOptionsResponse>(
        `/api/cycles/${cycleId}/shortfalls?date=${encodeURIComponent(date ?? "")}`,
      ),
  });
}

/**
 * Every read a ladder rung changes, in one place.
 *
 * Backlog #92 (checklist 06.19). A rung used to invalidate only the roster
 * and the attention chip, leaving the LADDER'S OWN read untouched — so with
 * two short cells open on one day, cancelling Ann's leave for the first cell
 * left the second cell still offering that same (now-declined) leave, and
 * the click came back `not_cancellable:declined`. The ladder read is keyed
 * per DAY, so one filter covers every cell of every day of this cycle.
 *
 * By PREDICATE / prefix throughout: the roster and coverage keys carry a
 * `show` slot and an accountId these hooks do not hold, so the older
 * `["cycle-roster", cycleId]` shorthand matched nothing.
 */
export function invalidateAfterRung(qc: ReturnType<typeof useQueryClient>, cycleId: string): void {
  // The write places a pin + an assignment, so the grid the lead is looking
  // at is now stale. The panel itself swaps in the re-read `cell` the
  // response carries; this catches the OTHER cells.
  qc.invalidateQueries(cycleRosterQueryFilter(cycleId));
  qc.invalidateQueries(shortfallOptionsQueryFilter(cycleId));
  // Covering a cell can empty the "Review cover" inbox: the backend drops an
  // MC whose vacated seats are no longer short (no audit row involved), so
  // the chip count must be re-read after every rung.
  qc.invalidateQueries({ queryKey: mcAttentionQueryKey(cycleId) });
  // …and "Coverage · N short" counts the same cells.
  qc.invalidateQueries(coverageReviewQueryFilter(cycleId));
  // #2211 — the team roster page reads the same rows by date range.
  qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "team-range-roster" });
}

/** Rung (a): call back a post-call person onto the short cell. */
export function useConfirmRecall(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<
    ShortfallWriteResponse,
    ApiError | Error,
    {
      date: string;
      station: string;
      session: string;
      membershipId: string;
      /** Set for a pooled-floor cell so the response's re-read `cell` is that floor. */
      floorGroup?: string | null;
    }
  >({
    mutationFn: ({ date, station, session, membershipId, floorGroup }) =>
      apiFetch<ShortfallWriteResponse>(`/api/cycles/${cycleId}/shortfalls/recall`, {
        method: "POST",
        body: JSON.stringify({
          date,
          station,
          session,
          membership_id: membershipId,
          floor_group: floorGroup ?? null,
        }),
      }),
    // The write places a pin + an assignment, so the grid the lead is
    // looking at is now stale — refetch it. The panel itself swaps in the
    // re-read `cell` the response carries.
    onSuccess: () => {
      invalidateAfterRung(qc, cycleId);
    },
  });
}

/** After a recall: give the person the offered day back — release their
 *  ward rows that day and record a confirmed rest day. #2211 — `kind:
 *  "call"` releases the post-call day of a call the person was activated
 *  onto (a standby swapped in): written even when a seat goes short, and in
 *  the window that owns the day (`restCycleId`, the offer's `cycle_id`). */
export function useConfirmRest(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<
    RestWriteResponse,
    ApiError | Error,
    {
      membershipId: string;
      recallDate: string;
      date: string;
      /** How many rest days the recall owed. >1 makes the response carry the
       *  NEXT day back (`next_offer`), so a two-day rest is given back in two
       *  clicks instead of one and a silent debt. */
      daysOwed?: number;
      /** #2211 — `call` for the rest after an activated call (released even
       *  over a gap); `call_later` for a day back later after a keep (a
       *  spare day, released only when it opens no gap). */
      kind?: "recall" | "call" | "call_later";
      /** #2211 — the window owning the rest day, when it is not this one. */
      restCycleId?: string | null;
    }
  >({
    mutationFn: ({ membershipId, recallDate, date, daysOwed, kind, restCycleId }) =>
      apiFetch<RestWriteResponse>(`/api/cycles/${restCycleId ?? cycleId}/shortfalls/rest`, {
        method: "POST",
        body: JSON.stringify({
          membership_id: membershipId,
          recall_date: recallDate,
          date,
          days_owed: daysOwed ?? 1,
          // Sent only when set: an API before #2211 refuses unknown fields.
          ...(kind && kind !== "recall" ? { kind } : {}),
        }),
      }),
    onSuccess: (_res, vars) => {
      invalidateAfterRung(qc, cycleId);
      if (vars.restCycleId && vars.restCycleId !== cycleId) {
        invalidateAfterRung(qc, vars.restCycleId);
      }
    },
  });
}

/** Rung (b): cancel a person's leave (confirmed → declined) and place them. */
export function useConfirmCancelLeave(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<
    ShortfallWriteResponse,
    ApiError | Error,
    {
      date: string;
      station: string;
      session: string;
      leaveId: string;
      floorGroup?: string | null;
    }
  >({
    mutationFn: ({ date, station, session, leaveId, floorGroup }) =>
      apiFetch<ShortfallWriteResponse>(`/api/cycles/${cycleId}/shortfalls/cancel-leave`, {
        method: "POST",
        body: JSON.stringify({
          date,
          station,
          session,
          leave_id: leaveId,
          floor_group: floorGroup ?? null,
        }),
      }),
    onSuccess: () => {
      invalidateAfterRung(qc, cycleId);
    },
  });
}

/** Rung 0: seat someone who is simply free on the short cell — the lead's
 *  own hand placement, from the ladder. A 422 carries the scheduling rules
 *  the seat goes against (the grid's #1179 check); re-send with
 *  `acknowledgeRules` to seat anyway. `actingUp` (#1962) seats someone the
 *  lead opened the seat to from another tier: the server skips the ward's
 *  eligibility check and counts the cover in their history. */
export function useConfirmSeat(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<
    SeatWriteResponse,
    ApiError | Error,
    {
      date: string;
      station: string;
      session: string;
      membershipId: string;
      floorGroup?: string | null;
      acknowledgeRules?: boolean;
      actingUp?: boolean;
    }
  >({
    mutationFn: ({
      date,
      station,
      session,
      membershipId,
      floorGroup,
      acknowledgeRules,
      actingUp,
    }) =>
      apiFetch<SeatWriteResponse>(`/api/cycles/${cycleId}/shortfalls/seat`, {
        method: "POST",
        body: JSON.stringify({
          date,
          station,
          session,
          membership_id: membershipId,
          floor_group: floorGroup ?? null,
          confirm_save_with_thresholds_outside: acknowledgeRules ?? false,
          // Sent only when set: an API from before #1962 refuses unknown fields.
          ...(actingUp ? { acting_up: true } : {}),
        }),
      }),
    onSuccess: (_res, vars) => {
      invalidateAfterRung(qc, cycleId);
      if (vars.actingUp) {
        // The team roster page reads by date range, and Setup → Members
        // shows the cover history this seat just counted.
        qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "team-range-roster" });
        qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "team-cover-history" });
      }
    },
  });
}

/** #2412 — Move here: move someone seated on another ward onto the short
 *  cell for that session. The write is held for Publish: members keep the
 *  old seat until the lead publishes. */
export function useMoveHere(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<
    MoveWriteResponse,
    ApiError | Error,
    {
      date: string;
      station: string;
      session: string;
      membershipId: string;
      floorGroup?: string | null;
      acknowledgeRules?: boolean;
    }
  >({
    mutationFn: ({ date, station, session, membershipId, floorGroup, acknowledgeRules }) =>
      apiFetch<MoveWriteResponse>(`/api/cycles/${cycleId}/shortfalls/move`, {
        method: "POST",
        body: JSON.stringify({
          date,
          station,
          session,
          membership_id: membershipId,
          floor_group: floorGroup ?? null,
          confirm_save_with_thresholds_outside: acknowledgeRules ?? false,
        }),
      }),
    onSuccess: () => {
      invalidateAfterRung(qc, cycleId);
    },
  });
}
